/**
 * Global constants shared across the application
 * These values are used in multiple components and should be centralized
 */
import manageableRoles from '@config/manageable_roles.json';
import fulfilmentRoles from '@config/fulfilment_roles.json';

export const ROLE_VALUES = ['member', 'rnd', 'storytelling', 'salesperson', 'sales_admin', 'sales_coordinator', 'admin', 'developer', 'fulfilment_admin', 'fulfilment_executive', 'coordinator', 'dispatcher', 'specialist', 'catering_assistant', 'packer', 'warehouse_assistant', 'inhouse_driver', 'finance', 'production'] as const;

/**
 * Roles that make up the sales team. Mirrors `User::Roles::SALES_TEAM` — these
 * are the only roles the public catering referral selector lists, so they're
 * also the only ones offered the referral visibility toggle (GRAIN-2887).
 */
export const SALES_TEAM_ROLES = ['salesperson', 'sales_admin', 'sales_coordinator'] as const;

/**
 * Team-admin role → the roles they may manage/assign. Single source of truth
 * shared with Ruby (`config/manageable_roles.json`, read by `User::Roles`), so
 * the dropdown and `UserPolicy#assign_role?` can never disagree.
 */
export const MANAGEABLE_ROLES: Record<string, readonly string[]> = manageableRoles;

export const FIELD_STAFF_ROLES = ['catering_assistant', 'inhouse_driver', 'specialist'] as const;

export function isFieldStaffRole(role: string): boolean {
  return FIELD_STAFF_ROLES.includes(role as (typeof FIELD_STAFF_ROLES)[number]);
}

export const FULFILMENT_ROLES: readonly string[] = fulfilmentRoles;

export function isFulfilmentRole(role: string): boolean {
  return FULFILMENT_ROLES.includes(role);
}

export const STATUS_VALUES = ['verified', 'unverified', 'closed'] as const;

export const CONFIRMATION_STATUS_VALUES = ['confirmed', 'unconfirmed'] as const;
export const ACCESS_LEVEL_VALUES = ['full_access', 'restricted'] as const;
export const EMPLOYMENT_TYPE_VALUES = ['full_time', 'part_time'] as const;

/** Default phone prefix for Singapore */
export const DEFAULT_PHONE_PREFIX = '+65';

/** Clearing PhoneInput leaves the bare dial code behind; that means no number. */
export function isBlankPhoneNumber(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  return trimmed === '' || /^\+\d{1,4}$/.test(trimmed);
}

/** Number of digits to pad user IDs */
export const USER_ID_PADDING = 3;

/**
 * Visible duration (ms) for a mutation-success confirmation toast — longer
 * than the toast default (5s) so users reliably notice their save/update
 * went through. Pass as `toast({..., duration: SUCCESS_TOAST_DURATION})`.
 */
export const SUCCESS_TOAST_DURATION = 10_000;

/** Avatar dimensions */
export const AVATAR_SIZE_SM = 36;
export const AVATAR_SIZE_MD = 166;

/** Sheet widths */
export const SHEET_WIDTH_EDIT = 600;
export const SHEET_WIDTH_ACTIVITY = 1296;

/** Status dropdown trigger width (OrderDetailsHeader) */
export const STATUS_DROPDOWN_TRIGGER_WIDTH = 184;

/** Search bar width (matches Figma design) */
export const SEARCH_BAR_WIDTH = 650;
